// Core
import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import * as ReactDOM from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { v4 as uuidv4 } from "uuid";

// Components
import {
  AnchorNav,
  Button,
  Chip,
  FieldSelect,
  FieldText,
  FileDrop,
  MiniTable,
  Modal,
  Toggle,
  ValidityDateRangePicker,
} from "components/AtomsV2";
import { BrandLogo, Icon, LanguageMenu } from "components/ShellV2";
import UploadHelpButton from "components/UploadHelpButton";

// Redux
import {
  openNotificationPopup,
  selectThemeMode,
  setThemeMode,
} from "state/application/applicationSlice";
import { useAddNewSimulatedCommunityMutation } from "state/simulator/simulatorApiSlice";

// Utils
import {
  ALLOCATION_TYPES,
  CommunityOrAggregatorTypes,
  MARKET_RATES,
  SIMULATOR_IMPORT_VERSION as IMPORT_VERSION,
  meterInstallationTypes,
  meterTypes,
  resourceTypes,
  voltageLevelTypes,
} from "utils/constants";
import ContractForm from "./Contracts/ContractView/Form";

// Exceptions
import ImportVersionError from "./ImportExport/exception";

const SIM_ANCHORS = [
  { id: "basic", icon: "badge", labelKey: "sidenav.general_info" },
  { id: "members", icon: "groups", labelKey: "sidenav.members" },
  { id: "meters", icon: "electric_meter", labelKey: "sidenav.meters" },
  { id: "resources", icon: "solar_power", labelKey: "sidenav.resources" },
  { id: "resp", icon: "lan", labelKey: "sidenav.resp" },
  { id: "contracts", icon: "receipt_long", labelKey: "sidenav.contracts" },
  { id: "prices", icon: "sell", labelKey: "sidenav.fixed_bilateral_prices" },
  { id: "files", icon: "folder", labelKey: "anchors.files" },
  { id: "io", icon: "import_export", labelKey: "anchors.io" },
];

const validityPeriodsOverlap = (left, right) =>
  (!left.end_date || right.start_date <= left.end_date) &&
  (!right.end_date || left.start_date <= right.end_date);

/* Simulator camelCase detail-field schema per resource type. labelKey maps to
   resources:new_resource.form.<labelKey>.label (same ids as the live app). */
const SIM_DETAIL_FIELDS = {
  SCPU: ["generationCapacity"],
  STORAGE: [
    "nominalCapacity",
    "maxPower",
    "chargingEfficiency",
    "dischargingEfficiency",
    "minSOC",
    "maxSOC",
    "degCost",
    "initSoc",
  ],
  EV: [
    "nominalCapacity",
    "maxPowerInput",
    "maxPowerOutput",
    "chargingEfficiency",
    "dischargingEfficiency",
    "minSOC",
    "maxSOC",
    "degCost",
    "desiredMinSOC",
    "initSoc",
  ],
  EWH: ["power", "capacity", "maxTemp", "defaultStartTemp", "targetTemp"],
  HP: [
    "powerRated",
    "capacityTank",
    "tempInlet",
    "tempDesired",
    "tempOutInit",
    "tempIndoorInit",
    "tempIndoorFinal",
    "tempIndoorMin",
    "tempIndoorMax",
    "tempOutMin",
    "tempOutMax",
    "uValue",
    "thermalResistance",
    "hRad",
    "areaRad",
  ],
  IC: ["sumCertifiedPower"],
};
SIM_DETAIL_FIELDS.IU = SIM_DETAIL_FIELDS.IC;

const MAX_ELEMENTS = 25;
const DEFAULT_START_DATE = "2026-01-01";
const DEFAULT_END_DATE = "2028-12-31";

const defaultValidity = (source = {}) => ({
  start_date: source.start_date ?? source.validity ?? DEFAULT_START_DATE,
  end_date: source.end_date || DEFAULT_END_DATE,
});

const blankMember = (validity = {}) => ({
  pre_id: uuidv4(),
  name: "",
  is_host: false,
  is_promoter: false,
  ...defaultValidity(validity),
});

const blankMeter = (validity = {}) => ({
  pre_id: uuidv4(),
  name: "",
  type: "CPE",
  cpe: "",
  installation_type: "IAC",
  production_identifier: "",
  ...defaultValidity(validity),
});

const blankResource = (validity = {}) => ({
  pre_id: uuidv4(),
  name: "",
  type: resourceTypes[2].value,
  sumCertifiedPower: 0,
  voltageLevel: voltageLevelTypes[2].value,
  generationCapacity: 0,
  nominalCapacity: 0,
  maxPower: 0,
  maxPowerInput: 0,
  maxPowerOutput: 0,
  power: 0,
  capacity: 0,
  maxTemp: 0,
  defaultStartTemp: 0,
  targetTemp: 0,
  chargingEfficiency: 0,
  dischargingEfficiency: 0,
  minSOC: 0,
  maxSOC: 0,
  desiredMinSOC: 0,
  degCost: 0,
  powerRated: 0,
  capacityTank: 0,
  tempInlet: 0,
  tempDesired: 0,
  tempOutInit: 0,
  tempIndoorInit: 0,
  tempIndoorFinal: 0,
  tempIndoorMin: 0,
  tempIndoorMax: 0,
  tempOutMin: 0,
  tempOutMax: 0,
  uValue: 0,
  thermalResistance: 0,
  hRad: 0,
  areaRad: 0,
  ownerships: [{ member: "", perc: 0, ...defaultValidity(validity) }],
  meter: "",
  ...defaultValidity(validity),
});

const blankPrice = (validity = {}) => ({
  provider_user_id: "",
  receiver_user_id: "",
  price: 0.08,
  ...defaultValidity(validity),
});

const ensureArray = (value) => (Array.isArray(value) ? value : []);

const getImportRef = (value) => {
  if (!value || typeof value !== "object") return value ?? "";
  return value.pre_id ?? value.id ?? value.identifier ?? value.name ?? "";
};

const addLookup = (lookup, key, value) => {
  if (key !== undefined && key !== null && key !== "") {
    lookup.set(String(key), value);
  }
};

const normalizeImportedMembers = (members = [], validity = {}) =>
  ensureArray(members).map((member) => {
    const preId = member.pre_id ?? member.id ?? uuidv4();
    return {
      ...blankMember(validity),
      ...member,
      pre_id: preId,
      name: member.name ?? "",
      is_host: Boolean(member.is_host),
      is_promoter: Boolean(member.is_promoter),
      ...defaultValidity({ ...validity, ...member }),
    };
  });

const normalizeImportedMeters = (meters = [], validity = {}) =>
  ensureArray(meters).map((meter) => {
    const preId = meter.pre_id ?? meter.id ?? uuidv4();
    return {
      ...blankMeter(validity),
      ...meter,
      pre_id: preId,
      name: meter.name ?? meter.identifier ?? "",
      cpe: getImportRef(meter.cpe ?? meter.cpe_id),
      production_identifier: meter.production_identifier ?? "",
      ...defaultValidity({ ...validity, ...meter }),
    };
  });

const buildImportLookup = (originalItems, normalizedItems, aliases) => {
  const lookup = new Map();
  normalizedItems.forEach((item, index) => {
    const original = originalItems[index] ?? {};
    addLookup(lookup, item.pre_id, item.pre_id);
    aliases.forEach((alias) => {
      addLookup(lookup, getImportRef(original[alias]), item.pre_id);
      addLookup(lookup, getImportRef(item[alias]), item.pre_id);
    });
  });
  return lookup;
};

const normalizeImportedResources = (resources = [], meterLookup, memberLookup, validity = {}) =>
  ensureArray(resources).map((resource) => {
    const meterRef = getImportRef(resource.meter ?? resource.meter_id ?? resource.meterId);
    const ownerships = ensureArray(resource.ownerships).map((ownership) => {
      const memberRef = getImportRef(
        ownership.member ?? ownership.user_id ?? ownership.member_id ?? ownership.userId
      );
      return {
        member: memberLookup.get(String(memberRef)) ?? memberRef ?? "",
        perc: ownership.perc ?? ownership.perc_ownership ?? ownership.percentage ?? 0,
        ...defaultValidity({ ...validity, ...resource, ...ownership }),
      };
    });

    return {
      ...blankResource(validity),
      ...resource,
      pre_id: resource.pre_id ?? resource.id ?? uuidv4(),
      meter: meterLookup.get(String(meterRef)) ?? meterRef ?? "",
      ownerships: ownerships.length ? ownerships : blankResource(validity).ownerships,
      ...defaultValidity({ ...validity, ...resource }),
    };
  });

const normalizeImportedResp = (resp = [], meterLookup, validity = {}) =>
  ensureArray(resp).map((item) => {
    const providerRef = getImportRef(item.provider_meter_id ?? item.providerMeterId);
    const receiverRef = getImportRef(item.receiver_meter_id ?? item.receiverMeterId);
    return {
      ...item,
      provider_meter_id: meterLookup.get(String(providerRef)) ?? providerRef ?? "",
      receiver_meter_id: meterLookup.get(String(receiverRef)) ?? receiverRef ?? "",
      is_active: item.is_active ?? true,
      ...defaultValidity({ ...validity, ...item }),
    };
  });

const normalizeImportedContracts = (contracts = [], memberLookup, validity = {}) =>
  ensureArray(contracts).map((contract) => {
    const memberRef = getImportRef(contract.member ?? contract.user_id ?? contract.userId);
    return {
      ...contract,
      member: memberLookup.get(String(memberRef)) ?? memberRef ?? "",
      ...defaultValidity({ ...validity, ...contract }),
    };
  });

const normalizeImportedPrices = (prices = [], validity = {}) =>
  ensureArray(prices).map((price) => ({
    ...blankPrice(validity),
    ...price,
    ...defaultValidity({ ...validity, ...price }),
  }));

/* Design's Section: card with icon title, optional subtitle and action slot. */
function Section({ id, icon, title, sub, action, children }) {
  return (
    <div className="rec-card rec-card-pad" id={`${id}-info`}>
      <div className="flex justify-between items-start gap-3 mb-4 flex-wrap">
        <div>
          <h3 className="rec-section-title !mb-0">
            <Icon name={icon} />
            {title}
          </h3>
          {sub && <div className="text-[12.5px] text-ink-3 mt-1">{sub}</div>}
        </div>
        {action}
      </div>
      {children}
    </div>
  );
}

Section.propTypes = {
  id: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  title: PropTypes.node.isRequired,
  sub: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node,
};

Section.defaultProps = { sub: null, action: null, children: null };

function Simulator() {
  const { t } = useTranslation(["simulator", "members", "meters", "resources", "common"]);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const preloaded = location.state?.preloadedData;

  const isDark = useSelector(selectThemeMode) === "dark";

  const [validationErrors, setValidationErrors] = useState([]);
  const [showAllErrors, setShowAllErrors] = useState(false);
  const [basicInfo, setBasicInfo] = useState(() => ({
    start_date: DEFAULT_START_DATE,
    end_date: DEFAULT_END_DATE,
    ...(preloaded?.basicInfo ?? {
      community_name: "Comunidade Simulada",
      community_identifier: "",
      allocation: "PROPORTIONAL",
      community_or_aggregator_type: "EGAC",
      market_rate: "VANILLA_IMR",
      market_divisor: 0.5,
      market_compensation: 0.5,
      market_small_increment: 0.1,
      market_fixed_price: 0.0805,
    }),
    ...defaultValidity(preloaded?.basicInfo),
  }));
  const initialValidity = defaultValidity(basicInfo);
  const [members, setMembers] = useState(() =>
    normalizeImportedMembers(preloaded?.members, initialValidity)
  );
  const [meters, setMeters] = useState(() =>
    normalizeImportedMeters(preloaded?.meters, initialValidity)
  );
  const [resources, setResources] = useState(() =>
    normalizeImportedResources(preloaded?.resources, new Map(), new Map(), initialValidity)
  );
  const [contracts, setContracts] = useState(() =>
    normalizeImportedContracts(preloaded?.contracts, new Map(), initialValidity)
  );
  const [resp, setResp] = useState(() =>
    normalizeImportedResp(preloaded?.resp, new Map(), initialValidity)
  );
  const [fixedBilateralPrices, setFixedBilateralPrices] = useState(
    normalizeImportedPrices(preloaded?.fixedBilateralPrices, initialValidity)
  );
  const [loadsFiles, setLoadsFiles] = useState([]);
  const [dsoFile, setDsoFile] = useState([]);

  const [respFilter, setRespFilter] = useState("");
  const [contractModal, setContractModal] = useState({ open: false, index: null });
  const [editingContract, setEditingContract] = useState(null);
  const [detailsIdx, setDetailsIdx] = useState(null);
  const importInputRef = useRef(null);

  const setB = (patch) => setBasicInfo((s) => ({ ...s, ...patch }));

  const cpeMeters = meters.filter((m) => m.type === "CPE" || m.type === "AGGREGATOR_METER");
  const getContractForMeter = (meter, index) =>
    contracts.find((contract) => contract.meter === meter?.pre_id) ??
    (contracts[index]?.meter ? null : contracts[index]);
  const memberOptions = members.map((m) => ({ label: m.name || "—", value: m.pre_id }));
  const getMemberName = (preId) => members.find((m) => m.pre_id === preId)?.name ?? preId;
  const getMeterName = (preId) => meters.find((m) => m.pre_id === preId)?.name ?? preId;

  // When meters are deleted or changed from CPE type, remove orphaned contracts
  // Contracts are positionally indexed to CPE meters (contracts[i] ↔ cpeMeters[i])
  useEffect(() => {
    const cpeCount = meters.filter(
      (meter) => meter.type === "CPE" || meter.type === "AGGREGATOR_METER"
    ).length;
    const cpeIds = new Set(
      meters
        .filter((meter) => meter.type === "CPE" || meter.type === "AGGREGATOR_METER")
        .map((meter) => meter.pre_id)
    );
    setContracts((prev) =>
      prev.filter((contract, index) =>
        contract.meter ? cpeIds.has(contract.meter) : index < cpeCount
      )
    );
  }, [meters]);

  // Keep the RESP pair list in sync with CPE meters (every ordered pair gets
  // an entry, defaulting to active; orphaned pairs are pruned).
  useEffect(() => {
    setResp((prevResp) => {
      const cpeOnly = meters.filter((item) => item.type === "CPE");
      let newResp = [...prevResp];
      for (let i = 0; i < cpeOnly.length; i += 1) {
        for (let j = i + 1; j < cpeOnly.length; j += 1) {
          if (
            !newResp.some(
              (item) =>
                (item.provider_meter_id === cpeOnly[j].pre_id &&
                  item.receiver_meter_id === cpeOnly[i].pre_id) ||
                (item.provider_meter_id === cpeOnly[i].pre_id &&
                  item.receiver_meter_id === cpeOnly[j].pre_id)
            )
          ) {
            newResp.push({
              provider_meter_id: cpeOnly[j].pre_id,
              receiver_meter_id: cpeOnly[i].pre_id,
              is_active: true,
              ...defaultValidity(basicInfo),
            });
          }
        }
      }
      newResp = newResp.filter(
        (item) =>
          cpeOnly.some((e) => e.pre_id === item.provider_meter_id) &&
          cpeOnly.some((e) => e.pre_id === item.receiver_meter_id)
      );
      return newResp;
    });
  }, [basicInfo, meters]);

  // Drop references to deleted meters/members from resources
  useEffect(() => {
    setResources((prev) =>
      prev.map((element) => {
        let next = element;
        if (next.meter && !meters.some((meter) => meter.pre_id === next.meter)) {
          next = { ...next, meter: "" };
        }
        if (
          next.ownerships.some(
            (o) => o.member && !members.some((member) => member.pre_id === o.member)
          )
        ) {
          next = {
            ...next,
            ownerships: next.ownerships.map((o) =>
              o.member && !members.some((member) => member.pre_id === o.member)
                ? { ...o, member: "", perc: 0 }
                : o
            ),
          };
        }
        return next;
      })
    );
  }, [members, meters]);

  // Removing a CPE meter removes its positionally-indexed contract
  const onRemoveMeter = (index, meter) => {
    const isCPE = meter?.type === "CPE" || meter?.type === "AGGREGATOR_METER";
    if (!isCPE) return;
    const cpeIndex =
      meters.slice(0, index + 1).filter((m) => m.type === "CPE" || m.type === "AGGREGATOR_METER")
        .length - 1;
    setContracts((prev) =>
      prev.filter((contract, i) =>
        contract.meter ? contract.meter !== meter.pre_id : i !== cpeIndex
      )
    );
  };

  const [addSimulatedCommunity] = useAddNewSimulatedCommunityMutation();

  const onSubmit = async () => {
    // Validate ALL data.
    const errorArray = [];
    const validateValidity = (item, label) => {
      if (!item?.start_date) {
        errorArray.push(
          `${label}: ${t("validation.start_date_required", {
            defaultValue: "start date is required",
          })}`
        );
      } else if (item.end_date && item.end_date < item.start_date) {
        errorArray.push(
          `${label}: ${t("validation.invalid_date_period", {
            defaultValue: "end date must be on or after start date",
          })}`
        );
      }
    };

    if (basicInfo.community_name === "") {
      errorArray.push(t("validation.community_name_required"));
    }
    if (!basicInfo.community_identifier?.trim()) {
      errorArray.push(t("validation.community_identifier_required"));
    }
    validateValidity(basicInfo, t("basic_info.title", { defaultValue: "Community" }));
    if (members.length === 0) {
      errorArray.push(t("validation.at_least_one_member"));
    }

    if (meters.length === 0) {
      errorArray.push(t("validation.at_least_one_meter"));
    }

    if (resources.length === 0) {
      errorArray.push(t("validation.at_least_one_resource"));
    }

    members.forEach((member, index) => {
      validateValidity(member, `${t("member_one", { ns: "common" })} ${index + 1}`);
      if (member.name === "") {
        errorArray.push(t("validation.member_name_required", { index: index + 1 }));
      }
    });

    meters.forEach((meter, index) => {
      validateValidity(meter, `${t("meter_one", { ns: "common" })} ${index + 1}`);
      if (meter.name === "") {
        errorArray.push(t("validation.meter_identifier_required", { index: index + 1 }));
      }
      if (meter.type === "INTERNAL" && meter.cpe === "") {
        errorArray.push(t("validation.internal_meter_missing_cpe", { index: index + 1 }));
      }
    });

    const seenResourceNames = new Set();
    const resourcePeriodsByMeter = new Map();
    resources.forEach((resource, index) => {
      validateValidity(resource, `${t("resource_one", { ns: "common" })} ${index + 1}`);
      if (resource.name === "") {
        errorArray.push(t("validation.resource_name_required", { index: index + 1 }));
      } else {
        const normalizedName = resource.name.trim().toLocaleLowerCase();
        if (seenResourceNames.has(normalizedName)) {
          errorArray.push(
            t("validation.resource_name_duplicate", {
              name: resource.name.trim(),
              defaultValue: `Resource name "${resource.name.trim()}" is duplicated.`,
            })
          );
        }
        seenResourceNames.add(normalizedName);
      }
      if (resource.type === "" || resource.type === undefined) {
        errorArray.push(t("validation.resource_type_required", { index: index + 1 }));
      }
      if (resource.meter) {
        const assignedMeter = meters.find(
          (meter) => String(meter.pre_id) === String(resource.meter)
        );
        if (assignedMeter && ["CPE", "INTERNAL"].includes(assignedMeter.type)) {
          const existingPeriods = resourcePeriodsByMeter.get(resource.meter) || [];
          if (
            resource.start_date &&
            existingPeriods.some((existing) => validityPeriodsOverlap(existing, resource))
          ) {
            errorArray.push(
              t("validation.resource_meter_overlap", {
                meter: assignedMeter.name,
              })
            );
          }
          existingPeriods.push(resource);
          resourcePeriodsByMeter.set(resource.meter, existingPeriods);
        }
      }
      // Check of all ownerships have been properly assigned
      let totalOwnershipsPerc = 0;
      resource.ownerships.forEach((ownership, ownershipIndex) => {
        validateValidity(
          ownership,
          `${t("resource_one", { ns: "common" })} ${index + 1} — ${t("single.ownerships.title", {
            ns: "resources",
            defaultValue: "ownership",
          })} ${ownershipIndex + 1}`
        );
        if (ownership.member === "") {
          errorArray.push(
            t("validation.resource_ownership_member_missing", {
              index: index + 1,
              ownershipIndex: ownershipIndex + 1,
            })
          );
        }
        totalOwnershipsPerc += parseInt(ownership.perc, 10);
      });
      if (totalOwnershipsPerc !== 100) {
        errorArray.push(t("validation.resource_ownership_not_100", { index: index + 1 }));
      }
    });

    // Contracts are optional: a community can be created with no contracts or
    // with only some CPEs assigned (the rest left "Por definir"). We only
    // validate the fields of contracts that were actually defined, below.
    contracts.forEach((contract, index) => {
      validateValidity(contract, `${t("contracts.title")} ${index + 1}`);
      if (
        contract.voltage_level === "BTN simples" ||
        contract.voltage_level === "BTN bi-horária" ||
        contract.voltage_level === "BTN< tri-horária"
      ) {
        if (
          ![1.15, 2.3, 3.45, 4.6, 5.75, 6.9, 10.35, 13.8, 17.25, 20.7].includes(
            contract.contracted_power
          )
        ) {
          errorArray.push(t("validation.contracted_power_invalid", { index: index + 1 }));
        }
      }
      if (contract.voltage_level === "BTN>") {
        if (![27.6, 34.5, 41.4].includes(contract.contracted_power)) {
          errorArray.push(t("validation.contracted_power_invalid", { index: index + 1 }));
        }
      }
      if (contract.voltage_level === "BTE") {
        if (contract.contracted_power <= 41.4) {
          errorArray.push(t("validation.contracted_power_invalid", { index: index + 1 }));
        }
      }

      if (contract.tariff_cycle === "") {
        errorArray.push(t("validation.contract_cycle_required", { index: index + 1 }));
      }
      if (contract.voltage_level === "") {
        errorArray.push(t("validation.contract_voltage_required", { index: index + 1 }));
      }
      if (contract.member === "") {
        errorArray.push(t("validation.contract_member_required", { index: index + 1 }));
      }
    });

    fixedBilateralPrices.forEach((pair, index) => {
      validateValidity(pair, `${t("sidenav.fixed_bilateral_prices")} ${index + 1}`);
      if (!pair.provider_user_id || !pair.receiver_user_id) {
        errorArray.push(t("fixedBilateralPrices.errors.selectBoth"));
      } else if (pair.provider_user_id === pair.receiver_user_id) {
        errorArray.push(t("fixedBilateralPrices.errors.sameUser"));
      } else if (
        fixedBilateralPrices.findIndex(
          (p) =>
            p.provider_user_id === pair.provider_user_id &&
            p.receiver_user_id === pair.receiver_user_id
        ) !== index
      ) {
        errorArray.push(t("fixedBilateralPrices.errors.duplicate"));
      }
      const parsedPrice = parseFloat(pair.price);
      if (Number.isNaN(parsedPrice) || parsedPrice < 0) {
        errorArray.push(t("fixedBilateralPrices.errors.invalidPrice"));
      }
    });

    resp.forEach((pair, index) => {
      validateValidity(pair, `${t("resp.title")} ${index + 1}`);
    });

    if (errorArray.length > 0) {
      setValidationErrors(errorArray);
      setShowAllErrors(false);
      return;
    }

    try {
      await addSimulatedCommunity({
        basicInfo,
        members,
        resources,
        meters,
        contracts,
        resp,
        fixedBilateralPrices: fixedBilateralPrices.map((p) => ({
          ...p,
          price: parseFloat(p.price),
        })),
        loadsFiles,
        dsoFile,
      }).unwrap();
      navigate(`/communities/`);
      dispatch(
        openNotificationPopup({
          notification: t("notifications.creating"),
          type: "success",
        })
      );
    } catch (err) {
      dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // ── Import / export ───────────────────────────────────────────────────────
  const handleExport = () => {
    const data = { basicInfo, members, resources, meters, contracts, resp, fixedBilateralPrices };
    data.import_version = IMPORT_VERSION;
    const json = JSON.stringify(data, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "data.json";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleImport = (file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const parsed = JSON.parse(event.target.result);
        const data = parsed.simulator ?? parsed;
        if (!data.import_version || data.import_version !== IMPORT_VERSION) {
          throw new ImportVersionError(t("import_export.errors.invalid_import_version"));
        }
        const importedBasicInfo = data.basicInfo ?? {};
        const importedValidity = defaultValidity(importedBasicInfo);
        const originalMembers = ensureArray(data.members);
        const originalMeters = ensureArray(data.meters);
        const importedMembers = normalizeImportedMembers(originalMembers, importedValidity);
        let importedMeters = normalizeImportedMeters(originalMeters, importedValidity);
        const memberLookup = buildImportLookup(originalMembers, importedMembers, [
          "pre_id",
          "id",
          "name",
        ]);
        const meterLookup = buildImportLookup(originalMeters, importedMeters, [
          "pre_id",
          "id",
          "name",
          "identifier",
        ]);
        importedMeters = importedMeters.map((meter) => ({
          ...meter,
          cpe: meterLookup.get(String(meter.cpe)) ?? meter.cpe,
        }));
        const importedResources = normalizeImportedResources(
          data.resources,
          meterLookup,
          memberLookup,
          importedValidity
        );
        const importedContracts = normalizeImportedContracts(
          data.contracts,
          memberLookup,
          importedValidity
        );
        const importedResp = normalizeImportedResp(data.resp, meterLookup, importedValidity);

        ReactDOM.unstable_batchedUpdates(() => {
          setBasicInfo((prev) => ({
            ...prev,
            ...importedBasicInfo,
            community_name:
              importedBasicInfo.community_name ??
              importedBasicInfo.name ??
              prev.community_name ??
              "",
            community_identifier:
              importedBasicInfo.community_identifier ??
              importedBasicInfo.identifier ??
              prev.community_identifier ??
              "",
            allocation:
              importedBasicInfo.allocation ??
              importedBasicInfo.method_alloc_coeff ??
              prev.allocation ??
              "PROPORTIONAL",
            ...importedValidity,
          }));
          setMembers(importedMembers);
          setMeters(importedMeters);
          setResources(importedResources);
          setContracts(importedContracts);
          setResp(importedResp);
          setFixedBilateralPrices(
            normalizeImportedPrices(data.fixedBilateralPrices, importedValidity)
          );
        });

        dispatch(
          openNotificationPopup({
            notification: t("import_export.notifications.success"),
            type: "success",
          })
        );
      } catch (e) {
        dispatch(
          openNotificationPopup({
            notification:
              e.name === "ImportVersionError"
                ? t("import_export.notifications.invalid_version")
                : t("import_export.notifications.error"),
            type: "error",
          })
        );
      }
    };
    reader.readAsText(file);
  };

  // ── Option lists ──────────────────────────────────────────────────────────
  const allocationOptions = ALLOCATION_TYPES.map((opt) => ({
    ...opt,
    label: t(`basic_info.options.allocation.${opt.value}`),
  }));
  const resourceTypeOptions = resourceTypes.map((o) => ({
    ...o,
    label: t(`new_resource.selects.types.${o.value}`, { ns: "resources" }),
  }));
  const yesNoOptions = [
    { value: "true", label: t("table.badges.yes", { ns: "members" }) },
    { value: "false", label: t("table.badges.no", { ns: "members" }) },
  ];
  const cpeOptions = [
    { value: "", label: "—" },
    ...meters
      .filter((m) => m.type === "CPE")
      .map((m) => ({ label: m.name || "—", value: m.pre_id })),
  ];
  const resourceMeterOptions = (row) => [
    { value: "", label: "—" },
    ...meters
      .filter((meter) => {
        if (!["CPE", "INTERNAL"].includes(meter.type)) return true;
        return !resources.some(
          (resource) =>
            resource !== row &&
            String(resource.meter) === String(meter.pre_id) &&
            resource.start_date &&
            row.start_date &&
            validityPeriodsOverlap(resource, row)
        );
      })
      .map((meter) => ({ label: meter.name || "—", value: meter.pre_id })),
  ];

  const boolColumn = (key, label) => ({
    key,
    label,
    type: "select",
    options: yesNoOptions,
    width: 130,
    getValue: (row) => String(row[key] ?? false),
    setValue: (row, v) => ({ ...row, [key]: v === "true" }),
  });
  const validityColumns = [
    {
      key: "validity",
      label: t("validity.period", { ns: "common", defaultValue: "Validity period" }),
      type: "validity",
      width: 300,
      getValue: (row) => ({ start: row.start_date || "", end: row.end_date || "" }),
      setValue: (row, value) => ({
        ...row,
        start_date: value.start,
        end_date: value.end || null,
      }),
    },
  ];

  const filteredResp = resp.filter((element) => {
    const filterLower = respFilter.toLowerCase();
    if (!filterLower) return true;
    return (
      getMeterName(element.provider_meter_id).toLowerCase().includes(filterLower) ||
      getMeterName(element.receiver_meter_id).toLowerCase().includes(filterLower)
    );
  });

  const toggleResp = (element) => {
    const index = resp.indexOf(element);
    setResp((prev) =>
      prev.map((item, i) => (i === index ? { ...item, is_active: !item.is_active } : item))
    );
  };
  const setRespField = (element, key, value) => {
    const index = resp.indexOf(element);
    setResp((prev) => prev.map((item, i) => (i === index ? { ...item, [key]: value } : item)));
  };

  const detailsResource = detailsIdx !== null ? resources[detailsIdx] : null;
  const setDetailField = (key, value) =>
    setResources((prev) => prev.map((r, i) => (i === detailsIdx ? { ...r, [key]: value } : r)));
  const setOwnership = (i, patch) =>
    setResources((prev) =>
      prev.map((r, ri) =>
        ri === detailsIdx
          ? { ...r, ownerships: r.ownerships.map((o, oi) => (oi === i ? { ...o, ...patch } : o)) }
          : r
      )
    );

  const contractMeterName = cpeMeters[contractModal.index]?.name;

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="rec-comm-screen">
      <header className="rec-comm-top">
        <BrandLogo variant="v2" height={56} forceWhite={isDark} />
        <div className="w-px h-[28px] bg-border ml-[6px] mr-[10px]" aria-hidden="true" />
        <Button variant="ghost" size="sm" onClick={() => navigate("/communities")}>
          <Icon name="arrow_back" />
          {t("header.back")}
        </Button>
        <div className="w-px h-[28px] bg-border mx-[6px]" aria-hidden="true" />
        <div className="flex items-center gap-[10px]">
          <div
            className="rec-kpi-ic rec-tint-info"
            style={{ width: 34, height: 34, borderRadius: 10 }}
          >
            <Icon name="science" fill style={{ fontSize: 19 }} />
          </div>
          <div className="leading-[1.2]">
            <div className="text-[14px] font-bold text-ink">{t("header.title")}</div>
            <div className="text-[11px] text-ink-3">{t("header.subtitle")}</div>
          </div>
        </div>
        <div className="flex-1" />
        <LanguageMenu />
        <button
          type="button"
          className="rec-icon-btn"
          onClick={() => dispatch(setThemeMode(isDark ? "light" : "dark"))}
          title={t("header.title")}
        >
          <Icon name={isDark ? "light_mode" : "dark_mode"} fill />
        </button>
        <Button onClick={onSubmit}>
          <Icon name="check" />
          {t("buttons.create_community")}
        </Button>
      </header>

      <div className="rec-comm-body">
        <div className="rec-sim-grid">
          <AnchorNav
            items={SIM_ANCHORS.map((a) => ({ id: a.id, icon: a.icon, label: t(a.labelKey) }))}
          />

          <div className="flex flex-col gap-rec">
            {/* ── Informação geral ─────────────────────────────── */}
            <Section
              id="basic"
              icon="badge"
              title={t("basic_info.title")}
              sub={t("sections.basic_sub")}
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
                <FieldText
                  label={t("basic_info.fields.community_name")}
                  value={basicInfo.community_name}
                  onChange={(v) => setB({ community_name: v })}
                />
                <FieldText
                  label={t("basic_info.fields.community_identifier")}
                  value={basicInfo.community_identifier}
                  onChange={(v) => setB({ community_identifier: v })}
                  placeholder="ex. PT-CER-0190"
                />
                <FieldSelect
                  label={t("basic_info.fields.allocation_type")}
                  value={basicInfo.allocation}
                  options={allocationOptions}
                  onChange={(v) => setB({ allocation: v })}
                />
                <FieldSelect
                  label={t("basic_info.fields.community_or_aggregator_type", {
                    defaultValue: "Tipo de Comunidade/Agregador",
                  })}
                  value={basicInfo.community_or_aggregator_type}
                  options={CommunityOrAggregatorTypes}
                  onChange={(v) => setB({ community_or_aggregator_type: v })}
                />
                <ValidityDateRangePicker
                  className="sm:col-span-2"
                  value={{ start: basicInfo.start_date, end: basicInfo.end_date ?? "" }}
                  onChange={({ start, end }) => setB({ start_date: start, end_date: end || null })}
                  portal
                />
              </div>
              <div className="rec-divider" />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
                <FieldSelect
                  label={t("basic_info.fields.market_rate", {
                    defaultValue: "Método de Preço Interno",
                  })}
                  value={basicInfo.market_rate}
                  options={MARKET_RATES}
                  onChange={(v) => setB({ market_rate: v })}
                />
                {basicInfo.market_rate === "VANILLA_IMR" && (
                  <FieldText
                    label={t("basic_info.fields.market_divisor", { defaultValue: "Divisor" })}
                    type="number"
                    min={0.01}
                    max={1}
                    step={0.01}
                    value={basicInfo.market_divisor ?? 0.5}
                    onChange={(v) => {
                      const value = Number(v);
                      setB({ market_divisor: value <= 0 ? 0.01 : Math.min(value, 1.0) });
                    }}
                  />
                )}
                {basicInfo.market_rate === "VANILLA_SDR" && (
                  <FieldText
                    label={t("basic_info.fields.market_compensation", {
                      defaultValue: "Compensação",
                    })}
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={basicInfo.market_compensation ?? 0.5}
                    onChange={(v) =>
                      setB({ market_compensation: Math.min(Math.max(Number(v), 0.0), 1.0) })
                    }
                  />
                )}
                {basicInfo.market_rate === "VANILLA_CV" && (
                  <FieldText
                    label={t("basic_info.fields.market_small_increment", {
                      defaultValue: "Incremento",
                    })}
                    type="number"
                    min={0}
                    max={1}
                    step={0.01}
                    value={basicInfo.market_small_increment ?? 0.1}
                    onChange={(v) => setB({ market_small_increment: Number(v) })}
                  />
                )}
                {basicInfo.market_rate === "FIXED_PRICE" && (
                  <FieldText
                    label={t("basic_info.fields.market_fixed_price", {
                      defaultValue: "Preço Fixo",
                    })}
                    type="number"
                    value={basicInfo.market_fixed_price ?? 0.0805}
                    onChange={(v) => setB({ market_fixed_price: Number(v) })}
                  />
                )}
              </div>
            </Section>

            {/* ── Membros ──────────────────────────────────────── */}
            <Section
              id="members"
              icon="groups"
              title={t("sidenav.members")}
              sub={t("sections.members_sub")}
            >
              <MiniTable
                rows={members}
                setRows={setMembers}
                blank={() => blankMember(basicInfo)}
                max={MAX_ELEMENTS}
                addLabel={t("members_add")}
                emptyLabel={t("table.empty")}
                removeLabel={t("table.remove")}
                columns={[
                  {
                    key: "name",
                    label: t("wizard.members.card.name_label", { ns: "members" }),
                    ph: "ex. Maria Albuquerque",
                  },
                  boolColumn(
                    "is_host",
                    t("single_member.general.labels.is_host", { ns: "members" })
                  ),
                  boolColumn(
                    "is_promoter",
                    t("single_member.general.labels.is_promoter", { ns: "members" })
                  ),
                  ...validityColumns,
                ]}
              />
            </Section>

            {/* ── Contadores ───────────────────────────────────── */}
            <Section
              id="meters"
              icon="electric_meter"
              title={t("sidenav.meters")}
              sub={t("sections.meters_sub")}
            >
              <MiniTable
                rows={meters}
                setRows={setMeters}
                blank={() => blankMeter(basicInfo)}
                max={MAX_ELEMENTS}
                addLabel={t("meters_add")}
                emptyLabel={t("table.empty")}
                removeLabel={t("table.remove")}
                onRemove={onRemoveMeter}
                columns={[
                  {
                    key: "name",
                    label: t("modal.new.identifier", { ns: "meters" }),
                    ph: "PT0002 0410 ...",
                  },
                  {
                    key: "type",
                    label: t("modal.new.type", { ns: "meters" }),
                    type: "select",
                    options: meterTypes,
                    width: 130,
                  },
                  {
                    key: "installation_type",
                    label: t("modal.new.installation_type", { ns: "meters" }),
                    type: "select",
                    options: meterInstallationTypes,
                    width: 170,
                  },
                  {
                    key: "cpe",
                    label: t("modal.new.cpe", { ns: "meters" }),
                    type: "select",
                    options: cpeOptions,
                    width: 150,
                    disabled: (row) => row.type !== "INTERNAL",
                  },
                  {
                    key: "production_identifier",
                    label: t("modal.new.production_identifier", { ns: "meters" }),
                    width: 150,
                    disabled: (row) => !(row.type === "CPE" && row.installation_type === "IAC"),
                  },
                  ...validityColumns,
                ]}
              />
            </Section>

            {/* ── Recursos ─────────────────────────────────────── */}
            <Section
              id="resources"
              icon="solar_power"
              title={t("sidenav.resources")}
              sub={t("sections.resources_sub")}
            >
              <MiniTable
                rows={resources}
                setRows={setResources}
                blank={() => blankResource(basicInfo)}
                max={MAX_ELEMENTS}
                addLabel={t("resources_add")}
                emptyLabel={t("table.empty")}
                removeLabel={t("table.remove")}
                rowActions={(row, i) => (
                  <button
                    type="button"
                    className="rec-icon-btn !w-[34px] !h-[34px]"
                    title={`${t("resources_details")} — ${row.name || `#${i + 1}`}`}
                    onClick={() => setDetailsIdx(i)}
                  >
                    <Icon name="tune" style={{ fontSize: 18 }} />
                  </button>
                )}
                columns={[
                  {
                    key: "name",
                    label: t("new_resource.form.designation.label", { ns: "resources" }),
                    ph: "ex. UPAC Norte",
                  },
                  {
                    key: "type",
                    label: t("new_resource.form.type.label", { ns: "resources" }),
                    type: "select",
                    options: resourceTypeOptions,
                    width: 160,
                  },
                  {
                    key: "meter",
                    label: t("meter_one", { ns: "common" }),
                    type: "select",
                    options: resourceMeterOptions,
                    width: 150,
                  },
                  {
                    key: "owner",
                    label: t("member_one", { ns: "common" }),
                    type: "select",
                    options: [{ value: "", label: "—" }, ...memberOptions],
                    width: 150,
                    getValue: (row) => row.ownerships?.[0]?.member ?? "",
                    setValue: (row, v) => ({
                      ...row,
                      ownerships: [
                        { ...(row.ownerships?.[0] || { perc: 0 }), member: v },
                        ...(row.ownerships?.slice(1) || []),
                      ],
                    }),
                  },
                  {
                    key: "perc",
                    label: t("create.ownership.allocation_label", { ns: "resources" }),
                    type: "number",
                    width: 90,
                    getValue: (row) => row.ownerships?.[0]?.perc ?? 0,
                    setValue: (row, v) => ({
                      ...row,
                      ownerships: [
                        { ...(row.ownerships?.[0] || { member: "" }), perc: v },
                        ...(row.ownerships?.slice(1) || []),
                      ],
                    }),
                  },
                  ...validityColumns,
                ]}
              />
            </Section>

            {/* ── RESP ─────────────────────────────────────────── */}
            <Section
              id="resp"
              icon="lan"
              title={t("resp.title")}
              sub={t("sections.resp_sub")}
              action={
                resp.length > 0 ? (
                  <div className="rec-input-wrap w-[200px]">
                    <Icon name="search" />
                    <input
                      className="rec-input"
                      style={{ height: 36, fontSize: 13 }}
                      value={respFilter}
                      placeholder={t("resp.filter_placeholder")}
                      onChange={(e) => setRespFilter(e.target.value)}
                    />
                  </div>
                ) : null
              }
            >
              {resp.length === 0 ? (
                <div className="text-[13px] text-ink-3">{t("resp.empty")}</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {filteredResp.map((element) => (
                    <div
                      key={`resp-${element.provider_meter_id}-${element.receiver_meter_id}`}
                      className="flex items-center gap-3"
                      style={{
                        padding: "12px 14px",
                        background: "var(--surface-2)",
                        borderRadius: "var(--radius-sm)",
                      }}
                    >
                      <Icon name="electric_meter" style={{ color: "var(--accent)" }} />
                      <span className="flex-1 text-[13px] text-ink">
                        {getMeterName(element.provider_meter_id)}
                        <span className="mx-[6px] text-ink-3 text-[12px]">→</span>
                        {getMeterName(element.receiver_meter_id)}
                      </span>
                      <ValidityDateRangePicker
                        label={null}
                        value={{
                          start: element.start_date ?? "",
                          end: element.end_date ?? "",
                        }}
                        onChange={({ start, end }) => {
                          setRespField(element, "start_date", start);
                          setRespField(element, "end_date", end || null);
                        }}
                        portal
                      />
                      <Toggle
                        on={Boolean(element.is_active)}
                        onClick={() => toggleResp(element)}
                        ariaLabel={t("resp.label")}
                      />
                    </div>
                  ))}
                </div>
              )}
            </Section>

            {/* ── Contratos ────────────────────────────────────── */}
            <Section
              id="contracts"
              icon="receipt_long"
              title={t("contracts.title")}
              sub={t("sections.contracts_sub")}
            >
              {cpeMeters.length === 0 ? (
                <div className="text-[13px] text-ink-3">{t("contracts.subtitle_edit")}</div>
              ) : (
                <div className="flex flex-col gap-2">
                  {cpeMeters.map((meter, index) => {
                    const contract = getContractForMeter(meter, index);
                    return (
                      <div
                        key={meter.pre_id}
                        className="flex items-center gap-3 flex-wrap"
                        style={{
                          padding: "12px 14px",
                          background: "var(--surface-2)",
                          borderRadius: "var(--radius-sm)",
                        }}
                      >
                        <Icon name="receipt_long" style={{ color: "var(--accent)" }} />
                        <span className="text-[13px] font-semibold text-ink">
                          {meter.name || "—"}
                        </span>
                        <div className="flex items-center gap-[6px] flex-wrap">
                          {contract ? (
                            <>
                              {contract.voltage_level && (
                                <Chip tone="gray">{contract.voltage_level}</Chip>
                              )}
                              {contract.contracted_power > 0 && (
                                <Chip tone="gray">{`${contract.contracted_power} kVA`}</Chip>
                              )}
                              {contract.tariff_cycle && (
                                <Chip tone="gray">{contract.tariff_cycle}</Chip>
                              )}
                              {contract.member && (
                                <Chip tone="teal">{getMemberName(contract.member)}</Chip>
                              )}
                            </>
                          ) : (
                            <Chip tone="amber">{t("contracts_not_defined")}</Chip>
                          )}
                        </div>
                        <div className="ml-auto">
                          <Button
                            variant="ghost"
                            size="sm"
                            title={t("contracts.button_text", { identifier: meter.name })}
                            onClick={() => {
                              setEditingContract(contract || null);
                              setContractModal({ open: true, index });
                            }}
                          >
                            <Icon name="edit" />
                            {t("contracts_edit")}
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </Section>

            {/* ── Preços bilaterais fixos ──────────────────────── */}
            <Section
              id="prices"
              icon="sell"
              title={t("sidenav.fixed_bilateral_prices")}
              sub={t("sections.prices_sub")}
            >
              <MiniTable
                rows={fixedBilateralPrices}
                setRows={setFixedBilateralPrices}
                blank={() => blankPrice(basicInfo)}
                addLabel={t("prices_add")}
                emptyLabel={t("table.empty")}
                removeLabel={t("table.remove")}
                columns={[
                  {
                    key: "provider_user_id",
                    label: t("fixedBilateralPrices.form.provider"),
                    type: "select",
                    options: [{ value: "", label: "—" }, ...memberOptions],
                    width: 180,
                  },
                  {
                    key: "receiver_user_id",
                    label: t("fixedBilateralPrices.form.receiver"),
                    type: "select",
                    options: [{ value: "", label: "—" }, ...memberOptions],
                    width: 180,
                  },
                  {
                    key: "price",
                    label: t("fixedBilateralPrices.form.price"),
                    type: "number",
                    width: 140,
                  },
                  ...validityColumns,
                ]}
              />
            </Section>

            {/* ── Ficheiros ────────────────────────────────────── */}
            <Section
              id="files"
              icon="folder"
              title={t("loads_files.title")}
              sub={t("sections.files_sub")}
            >
              <FileDrop
                icon="show_chart"
                title={t("loads_files.title")}
                hint=".csv"
                multiple
                files={loadsFiles}
                setFiles={setLoadsFiles}
                buttonLabel={t("files_upload")}
              />
              <FileDrop
                icon="electric_bolt"
                title={t("dso_file.title")}
                hint=".csv"
                files={dsoFile}
                setFiles={setDsoFile}
                buttonLabel={t("files_upload")}
              />
            </Section>

            {/* ── Importar / exportar ──────────────────────────── */}
            <Section
              id="io"
              icon="import_export"
              title={t("import_export.title")}
              sub={t("sections.io_sub")}
              action={
                <div className="flex gap-[10px]">
                  <Button variant="ghost" size="sm" onClick={handleExport}>
                    <Icon name="download" />
                    {t("io_export")}
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => importInputRef.current?.click()}>
                    <Icon name="upload_file" />
                    {t("io_import")}
                  </Button>
                  <UploadHelpButton />
                  <input
                    ref={importInputRef}
                    type="file"
                    accept="application/json"
                    className="hidden"
                    onChange={(e) => {
                      handleImport(e.target.files?.[0]);
                      e.target.value = "";
                    }}
                  />
                </div>
              }
            >
              <div
                className="flex items-center gap-[10px] text-[12.5px] text-ink-2"
                style={{
                  padding: "12px 14px",
                  background: "var(--surface-2)",
                  borderRadius: "var(--radius-sm)",
                }}
              >
                <Icon name="info" style={{ color: "var(--accent)" }} />
                {t("io_note")}
              </div>
            </Section>

            {/* ── Erros de validação ───────────────────────────── */}
            {validationErrors.length > 0 && (
              <div
                className="rec-card rec-card-pad"
                style={{
                  border: "1px solid var(--danger)",
                  background: "rgba(229, 99, 91, 0.06)",
                }}
              >
                <ol className="m-0 pl-5 flex flex-col gap-1">
                  {(showAllErrors ? validationErrors : validationErrors.slice(0, 5)).map(
                    (error) => (
                      <li key={error} className="text-[13px] font-semibold text-danger">
                        {error}
                      </li>
                    )
                  )}
                </ol>
                {!showAllErrors && validationErrors.length > 5 && (
                  <button
                    type="button"
                    className="bg-transparent border-0 p-0 mt-2 cursor-pointer text-[13px] font-semibold text-danger underline"
                    onClick={() => setShowAllErrors(true)}
                  >
                    {t("validation.show_more_errors", { count: validationErrors.length - 5 })}
                  </button>
                )}
              </div>
            )}

            {/* ── Footer actions ───────────────────────────────── */}
            <div className="flex justify-end gap-[10px]">
              <Button variant="ghost" onClick={() => navigate("/communities")}>
                {t("cancel", { ns: "common" })}
              </Button>
              <Button onClick={onSubmit}>
                <Icon name="check" />
                {t("buttons.create_community")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Contract editor modal (existing MUI form preserved) ── */}
      <Modal
        open={contractModal.open}
        onClose={() => {
          setContractModal({ open: false, index: null });
          setEditingContract(null);
        }}
        title={
          contractMeterName
            ? `${t("contracts.title")} — ${contractMeterName}`
            : t("contracts.title")
        }
        maxWidth={1100}
        footer={
          <>
            <Button
              variant="ghost"
              onClick={() => {
                setContractModal({ open: false, index: null });
                setEditingContract(null);
              }}
            >
              {t("cancel", { ns: "common" })}
            </Button>
            <Button
              onClick={() => {
                if (contractModal.index !== null && editingContract) {
                  setContracts((prev) => {
                    const newContracts = [...prev];
                    const meterId = cpeMeters[contractModal.index]?.pre_id;
                    const explicitIndex = newContracts.findIndex(
                      (contract) => contract.meter === meterId
                    );
                    const legacyIndex =
                      explicitIndex < 0 && !newContracts[contractModal.index]?.meter
                        ? contractModal.index
                        : -1;
                    const targetIndex = explicitIndex >= 0 ? explicitIndex : legacyIndex;
                    const nextContract = {
                      ...editingContract,
                      meter: meterId,
                    };
                    if (targetIndex >= 0) newContracts[targetIndex] = nextContract;
                    else newContracts.push(nextContract);
                    return newContracts;
                  });
                }
                setContractModal({ open: false, index: null });
                setEditingContract(null);
              }}
            >
              {t("confirm", { ns: "common" })}
            </Button>
          </>
        }
      >
        {contractModal.open && (
          <ContractForm
            contract={getContractForMeter(cpeMeters[contractModal.index], contractModal.index)}
            index={contractModal.index}
            members={members}
            onContractChange={setEditingContract}
            defaultStartDate={basicInfo.start_date}
            defaultEndDate={basicInfo.end_date}
          />
        )}
      </Modal>

      {/* ── Resource technical-details modal ── */}
      <Modal
        open={detailsIdx !== null}
        onClose={() => setDetailsIdx(null)}
        title={detailsResource?.name || t("resources_details")}
        subtitle={`${
          resourceTypeOptions.find((o) => o.value === detailsResource?.type)?.label ?? ""
        } · ${t("resources_details")}`}
        maxWidth={680}
        footer={
          <Button onClick={() => setDetailsIdx(null)}>{t("confirm", { ns: "common" })}</Button>
        }
      >
        {detailsResource && (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[14px]">
              {(SIM_DETAIL_FIELDS[detailsResource.type] || []).map((key) => (
                <FieldText
                  key={key}
                  label={t(`new_resource.form.${key}.label`, { ns: "resources" })}
                  type="number"
                  value={detailsResource[key] ?? 0}
                  onChange={(v) => setDetailField(key, v)}
                />
              ))}
              {(detailsResource.type === "IC" || detailsResource.type === "IU") && (
                <FieldSelect
                  label={t("new_resource.form.powerVoltageLevel.label", { ns: "resources" })}
                  value={detailsResource.voltageLevel}
                  options={voltageLevelTypes}
                  onChange={(v) => setDetailField("voltageLevel", v)}
                />
              )}
            </div>

            <div className="rec-divider" />
            <div className="flex items-center gap-[10px] mb-[10px]">
              <span className="text-[12.5px] font-semibold text-ink-2">
                {t("single.ownerships.title", { ns: "resources" })}
              </span>
              <Chip
                tone={
                  detailsResource.ownerships.reduce((s, o) => s + (+o.perc || 0), 0) === 100
                    ? "green"
                    : "gray"
                }
              >
                {`${detailsResource.ownerships.reduce((s, o) => s + (+o.perc || 0), 0)}%`}
              </Chip>
            </div>
            <div className="rec-owner-list">
              {detailsResource.ownerships.map((o, i) => (
                // eslint-disable-next-line react/no-array-index-key
                <div key={i} className="rec-owner-row">
                  <select
                    className="rec-input"
                    style={{ height: 36, fontSize: 13 }}
                    value={o.member}
                    onChange={(e) => setOwnership(i, { member: e.target.value })}
                  >
                    <option value="">—</option>
                    {memberOptions.map((m) => (
                      <option key={m.value} value={m.value}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                  <div className="rec-input-wrap w-[110px] shrink-0">
                    <input
                      className="rec-input"
                      type="number"
                      min="0"
                      max="100"
                      style={{ height: 36, fontSize: 13, paddingRight: 30 }}
                      value={o.perc}
                      onChange={(e) => setOwnership(i, { perc: e.target.value })}
                    />
                    <span className="absolute right-[11px] text-[12px] font-semibold text-ink-3">
                      %
                    </span>
                  </div>
                  <ValidityDateRangePicker
                    label={null}
                    value={{ start: o.start_date ?? "", end: o.end_date ?? "" }}
                    onChange={({ start, end }) =>
                      setOwnership(i, { start_date: start, end_date: end || null })
                    }
                    portal
                  />
                  {detailsResource.ownerships.length > 1 && (
                    <button
                      type="button"
                      className="rec-icon-btn !w-[34px] !h-[34px]"
                      title={t("table.remove")}
                      onClick={() =>
                        setResources((prev) =>
                          prev.map((r, ri) =>
                            ri === detailsIdx
                              ? { ...r, ownerships: r.ownerships.filter((_, oi) => oi !== i) }
                              : r
                          )
                        )
                      }
                    >
                      <Icon name="delete" style={{ fontSize: 18 }} />
                    </button>
                  )}
                </div>
              ))}
            </div>
            <Button
              variant="ghost"
              size="sm"
              disabled={detailsResource.ownerships.length >= 6}
              onClick={() =>
                setResources((prev) =>
                  prev.map((r, ri) =>
                    ri === detailsIdx
                      ? {
                          ...r,
                          ownerships: [
                            ...r.ownerships,
                            { member: "", perc: 0, ...defaultValidity(r) },
                          ],
                        }
                      : r
                  )
                )
              }
            >
              <Icon name="person_add" />
              {t("new_resource.owners_add", { ns: "resources" })}
            </Button>
          </>
        )}
      </Modal>
    </div>
  );
}

export default Simulator;
